import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  Badge,
  Modal,
  Button,
  buttonClass,
  Section,
  Toggle,
  useToast,
  type Requirement,
} from "@/components/ui";
import { domainKeys, domainsApi, has, useDomainDetail, type DomainDetail } from "@/lib/domains";
import { formatDateTime } from "@/lib/format";

import {
  SecurityStatusRows,
  dnssecPill,
  pendingOp,
  shownError,
  useAccount,
  useConfirmed,
} from "../security/shared";
import { TwoColumns, RuleRow, Notice } from "@/components/domains/parts";

/** Figma "Domain Control Centre — Security" (2043-4). */
export function SecurityTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { run, modal } = useConfirmed();
  const me = useAccount();
  const [logOpen, setLogOpen] = useState(false);
  const manage: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };

  const lockOp = pendingOp(detail, "transfer_lock");
  const dnssecOp = pendingOp(detail, "dnssec_on", "dnssec_off");
  // Keep the shell's detail fresh while the registry works on a change.
  useDomainDetail(d.id, { poll: Boolean(lockOp || dnssecOp) });

  const refresh = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  const toast = useToast();
  const lock = useMutation({
    mutationFn: (locked: boolean) =>
      run(() => domainsApi.setTransferLock(orgId, d.id, locked), {
        subtitle: `Unlocking lets ${d.display_name} move to another registrar — we verify identity first`,
        label: "Verify & unlock",
      }),
    onSuccess: async (_, locked) => {
      await refresh();
      toast.show({ tone: "success", title: locked ? "Transfer lock turned on" : "Transfer lock turned off" });
    },
  });
  const dnssec = useMutation({
    mutationFn: (enabled: boolean) => domainsApi.setDnssec(orgId, d.id, enabled),
    onSuccess: async (op) => {
      await refresh();
      navigate(`/domains/${d.id}/dnssec?op=${encodeURIComponent(op.id)}`);
    },
  });

  const canLock = has(detail, "transfer_lock");
  const unlockBlocked = d.transfer_lock && !d.transfer_window_open;
  const dnssecSupported = has(detail, "dnssec");
  const customNs = d.nameserver_mode === "custom";
  const dnssecState = dnssecPill(d.dnssec);
  const busyDnssec = d.dnssec === "enabling" || d.dnssec === "disabling";

  return (
    <>
      <TwoColumns
        main={
          <>
            <Section
              title={
                <>
                  Transfer lock <HelpHint helpKey={HELP_KEYS.domains.transferLock} label="transfer lock" />
                </>
              }
              subtitle={
                !canLock
                  ? `The .${d.tld} registry doesn't offer a transfer lock, so it can't be changed here.`
                  : unlockBlocked
                    ? "Blocks this domain from being transferred to another registrar. Registries don't allow transfers in the 60 days after a registration or transfer, so it stays on for now."
                    : "Blocks this domain from being transferred to another registrar without your action. Recommended: on."
              }
              action={
                canLock && (
                  <div className="flex items-center gap-2.5">
                    {lockOp && <span className="text-[13px] font-medium text-[#853f00]">Updating…</span>}
                    <Toggle
                      size="lg"
                      checked={lockOp ? Boolean(lockOp.detail.locked) : d.transfer_lock}
                      label="Transfer lock"
                      requires={manage}
                      busy={lock.isPending}
                      disabledReason={
                        lockOp
                          ? "Waiting for the registry to confirm the last change"
                          : unlockBlocked
                            ? "Registries don't allow transfers in the 60 days after a registration or transfer"
                            : undefined
                      }
                      onChange={(next) => lock.mutate(next)}
                    />
                  </div>
                )
              }
            >
              <ErrorAlert error={shownError(lock.error)} />
            </Section>

            <Section
              title={
                <>
                  DNSSEC <HelpHint helpKey={HELP_KEYS.domains.dnssec} label="DNSSEC" />
                </>
              }
              subtitle={
                !dnssecSupported
                  ? `The .${d.tld} registry doesn't support DNSSEC through CortexGrip.`
                  : customNs
                    ? "Cryptographically signs your DNS responses to prevent spoofing. This domain uses custom nameservers, so DNSSEC has to be set up with that DNS provider."
                    : "Cryptographically signs your DNS responses to prevent spoofing. Supported for this TLD."
              }
              action={
                <div className="flex items-center gap-2.5">
                  <Badge size="md" tone={dnssecSupported ? dnssecState.tone : "neutral"}>
                    {dnssecSupported ? dnssecState.label : "Not supported"}
                  </Badge>
                  {dnssecSupported && !customNs && (
                    <>
                      {busyDnssec ? (
                        <Link
                          to={`/domains/${d.id}/dnssec${dnssecOp ? `?op=${encodeURIComponent(dnssecOp.id)}` : ""}`}
                          className={buttonClass("secondary", "lg")}
                        >
                          View progress
                        </Link>
                      ) : d.dnssec === "on" ? (
                        <Button
                          size="lg"
                          variant="secondary"
                          requires={manage}
                          loading={dnssec.isPending}
                          onClick={() => dnssec.mutate(false)}
                        >
                          Turn off
                        </Button>
                      ) : (
                        <Button
                          size="lg"
                          variant="secondary"
                          requires={manage}
                          loading={dnssec.isPending}
                          onClick={() => dnssec.mutate(true)}
                        >
                          Enable
                        </Button>
                      )}
                    </>
                  )}
                </div>
              }
            >
              <ErrorAlert error={dnssec.error} />
            </Section>

            <Section title="Account protection">
              <RuleRow first>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-fg">Two-factor authentication</p>
                  <p className="text-xs text-fg-subtle">
                    {me.data?.mfa_enabled
                      ? "Asked for again before sensitive domain actions"
                      : "Not set up — recommended for anyone who manages domains"}
                  </p>
                </div>
                {me.data && !me.data.mfa_enabled && (
                  <Link to="/settings" className="text-[13px] font-semibold text-brand-500 hover:underline">
                    Turn on
                  </Link>
                )}
                {me.data && (
                  <Badge size="md" tone={me.data.mfa_enabled ? "success" : "warning"}>
                    {me.data.mfa_enabled ? "On" : "Off"}
                  </Badge>
                )}
              </RuleRow>
              <RuleRow>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-fg">Recent security actions</p>
                  <p className="text-xs text-fg-subtle">
                    Transfer lock, DNSSEC and authorization code changes
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setLogOpen(true)}
                  className="inline-flex items-center gap-[5px] rounded-full bg-[#f3ebe7] py-1 pr-[9px] pl-2 text-xs font-semibold text-[#5c5551] hover:bg-[#e7ded9]"
                >
                  <span aria-hidden className="size-[7px] rounded-full bg-current" />
                  View log
                </button>
              </RuleRow>
            </Section>
          </>
        }
        side={
          <>
            <div className="rounded-[14px] bg-[#faf6ef] p-4 shadow-[0_6px_10px_rgba(26,20,18,0.08)]">
              <p className="text-[15px] font-semibold text-fg">Why this matters</p>
              <p className="mt-2 text-[13px] text-[#766e6a]">
                Domain hijacking usually starts with a transfer request. Transfer lock plus two-factor
                authentication is the strongest protection you can enable here.
              </p>
            </div>
            <Section title="Security status">
              <SecurityStatusRows detail={detail} />
            </Section>
          </>
        }
      />
      <SecurityLog detail={detail} open={logOpen} onClose={() => setLogOpen(false)} />
      {modal}
    </>
  );
}

function SecurityLog({
  detail,
  open,
  onClose,
}: {
  detail: DomainDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { orgId } = useOrganization();
  const d = detail.domain;
  const events = useQuery({
    queryKey: [...domainKeys.all(orgId), "events", d.id, "security"],
    queryFn: () => domainsApi.events(orgId, d.id, "security"),
    enabled: open,
  });
  const list = events.data?.events ?? [];
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Recent security actions"
      subtitle={`Security changes on ${d.display_name}, newest first`}
      width={540}
    >
      {events.isPending && <p className="text-sm text-fg-subtle">Loading…</p>}
      <ErrorAlert error={events.error} />
      {events.isSuccess && list.length === 0 && (
        <Notice tone="cream" title="Nothing yet">
          Transfer lock, DNSSEC and authorization code changes will show up here.
        </Notice>
      )}
      {list.length > 0 && (
        <div className="flex flex-col gap-3.5">
          {list.map((e, i) => (
            <RuleRow key={e.id} first={i === 0} className="items-start">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-fg">{e.summary}</p>
                <p className="text-xs text-fg-subtle">
                  {formatDateTime(e.created_at)}
                  {e.actor_email ? ` · ${e.actor_email}` : ""}
                </p>
              </div>
            </RuleRow>
          ))}
        </div>
      )}
    </Modal>
  );
}
